import { keyColorsToDynamicFrame, mapImageBitmapToKeyColors, type ImageLightSource } from './frame_mapper'
import { imageLightMaxFrameCount } from './image_light_file'
import type { ImageLightOptions } from './types'
import type { DynamicImageFrame } from './dynamic_image_playback'

declare global {
  interface Window {
    ImageDecoder?: typeof ImageDecoder
  }
}

interface ImageDecodeFrameResult {
  image: ImageLightSource & { close?: () => void }
  duration?: number
}

interface ImageDecoder {
  tracks: {
    ready: Promise<void>
    selectedTrack?: {
      frameCount?: number
    }
  }
  decode(options?: { frameIndex?: number }): Promise<ImageDecodeFrameResult>
  close(): void
}

interface ImageDecoderInit {
  data: ArrayBuffer
  type: string
}

declare const ImageDecoder: {
  new (init: ImageDecoderInit): ImageDecoder
  isTypeSupported?: (type: string) => Promise<boolean>
}

interface DecodeDynamicImageFramesOptions {
  frameIntervalMs: number
  getOptions: () => ImageLightOptions
  isCurrentOperation: () => boolean
  setError: (errorKey: string, context: string, detail?: unknown) => void
}

export const decodeDynamicImageFrames = async (file: File, options: DecodeDynamicImageFramesOptions): Promise<DynamicImageFrame[] | null> => {
  if (!window.ImageDecoder) {
    options.setError('image_light_dynamic_decoder_missing', 'ImageDecoder is not available')
    return null
  }

  const fileType = file.type || 'image/gif'
  const supportsType = window.ImageDecoder.isTypeSupported ? await window.ImageDecoder.isTypeSupported(fileType) : true
  if (!supportsType) {
    options.setError('image_light_dynamic_type_error', 'animated image type is not supported by ImageDecoder', { name: file.name, type: fileType })
    return null
  }

  const decoder = new window.ImageDecoder({ data: await file.arrayBuffer(), type: fileType })
  try {
    await decoder.tracks.ready
    const frameCount = Math.min(decoder.tracks.selectedTrack?.frameCount || 1, imageLightMaxFrameCount)
    const frames: DynamicImageFrame[] = []

    for (let index = 0; index < frameCount; index++) {
      if (!options.isCurrentOperation()) return null
      const result = await decoder.decode({ frameIndex: index })
      try {
        if (!options.isCurrentOperation()) return null
        const colors = mapImageBitmapToKeyColors(result.image, options.getOptions())
        const durationMs = Math.round((result.duration || 100000) / 1000)
        frames.push({
          frame: keyColorsToDynamicFrame(colors),
          durationMs: Math.max(options.frameIntervalMs, durationMs),
        })
      } finally {
        result.image.close?.()
      }
    }

    return frames
  } finally {
    decoder.close()
  }
}
